import { CONTACT } from '../content/site.js';

/**
 * Privacy and Terms.
 *
 * The versions these replace described VedaJustice — a case-management
 * platform at vedajustice.com sending SMS case notifications — and said
 * nothing about ennoveda.net at all. That was almost certainly written for the
 * A2P 10DLC carrier registration and then attached to the wrong site.
 *
 * So both pages here do two things. They describe what this site actually does
 * with information (a contact form, a mailing list, a domain checker), and
 * they keep the SMS program language intact and clearly labelled, because the
 * carrier registration for that program very likely points at these URLs and
 * removing it could break a live campaign. Ennoveda Inc. operates both, so
 * both belong here — they just have to be told apart.
 */

const UPDATED = 'August 30, 2026';

function Address() {
  return (
    <p className="dim" style={{ lineHeight: 1.6 }}>
      {CONTACT.address.map((l) => <span key={l}>{l}<br /></span>)}
    </p>
  );
}

export function Privacy() {
  return (
    <>
      <section className="page-head">
        <div className="shell" style={{ maxWidth: '46rem' }}>
          <p className="eyebrow">Legal</p>
          <h1>Privacy Policy</h1>
          <p className="faint mono" style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
            Last updated: {UPDATED}
          </p>
        </div>
      </section>

      <section className="section" style={{ paddingTop: '2.5rem' }}>
        <div className="shell prose" style={{ maxWidth: '46rem' }}>
          <p>
            Ennoveda Inc. (“Ennoveda,” “we,” “us”) operates this website at ennoveda.net, and
            separately operates VedaJustice, a case-management platform for criminal-justice
            agencies at vedajustice.com. This policy covers both. Where a section applies to only
            one of them, it says so.
          </p>

          <h2>What this website collects</h2>
          <ul>
            <li>
              <strong>What you send us.</strong> If you use the contact form, we receive your name,
              email address, and anything else you choose to type — company, phone number, and your
              message. We use it to reply to you.
            </li>
            <li>
              <strong>Mailing list.</strong> If you subscribe to the Cyber Brief, we store your
              email address until you unsubscribe. Every issue carries an unsubscribe link.
            </li>
            <li>
              <strong>Domain checks.</strong> The domain checker stores the domain you looked up
              and the grade it received. It does not store the detailed findings, your IP address,
              or anything identifying you.
            </li>
            <li>
              <strong>Server logs.</strong> Our web server records requests in the ordinary way,
              including IP address and browser type, and these rotate out on a short cycle.
            </li>
          </ul>
          <p>
            This site sets no advertising cookies, runs no third-party analytics, and embeds no
            social tracking pixels.
          </p>

          <h2>How we use it</h2>
          <ul>
            <li>To reply to you, and to keep a record of the conversation.</li>
            <li>To send the Cyber Brief, if you asked for it.</li>
            <li>To operate and secure the site.</li>
            <li>To meet legal obligations.</li>
          </ul>
          <p>
            Enquiries are stored on our own server and mirrored to a Google Workspace spreadsheet
            we control, so the team can see them. We do not sell personal information, and we do
            not share it for anyone else's marketing.
          </p>

          <h2>SMS / text messaging — VedaJustice</h2>
          <p>
            This section applies to the VedaJustice platform, not to this website. Ennoveda Inc.
            operates the VedaJustice platform, which sends transactional SMS case notifications on
            behalf of the criminal-justice agencies that license it. When you opt in, we collect
            and process your mobile phone number, the fact and timestamp of your consent, and the
            notification types you agreed to receive, solely to deliver those case notifications.
          </p>
          <p>
            We do not sell, rent, or share your mobile phone number, SMS opt-in, or consent
            information with third parties or affiliates for marketing or promotional purposes. No
            mobile information will be shared with third parties or affiliates for marketing or
            promotional purposes.
          </p>
          <p>
            Message and data rates may apply. Message frequency varies. You may opt out at any
            time by replying STOP; reply HELP for help. To exercise any privacy right or ask a
            question, contact <a href={`mailto:${CONTACT.support}`}>{CONTACT.support}</a> or write
            to Ennoveda Inc., 4037 N. Chipwood Dr., Harvey, LA 70058.
          </p>

          <h2>How we share information</h2>
          <p>
            We share information only as necessary to operate our services — with infrastructure
            and messaging providers acting on our behalf under contract — to comply with the law,
            or to protect rights and safety. We do not sell personal information.
          </p>

          <h2>Data security and retention</h2>
          <p>
            We use administrative, technical, and physical safeguards designed to protect
            information, including encryption in transit and at rest. We retain information as
            long as needed to provide the service it relates to, and for VedaJustice, to meet the
            records-retention requirements applicable to criminal-justice agencies.
          </p>

          <h2>Your choices</h2>
          <p>
            You may unsubscribe from the Cyber Brief at any time from any issue, and opt out of
            VedaJustice SMS at any time by replying STOP. To ask what we hold about you, or to
            have it deleted, write to <a href={`mailto:${CONTACT.privacy}`}>{CONTACT.privacy}</a>.
          </p>

          <h2>Contact</h2>
          <Address />
          <p><a href={`mailto:${CONTACT.privacy}`}>{CONTACT.privacy}</a></p>
        </div>
      </section>
    </>
  );
}

export function Terms() {
  return (
    <>
      <section className="page-head">
        <div className="shell" style={{ maxWidth: '46rem' }}>
          <p className="eyebrow">Legal</p>
          <h1>Terms &amp; Conditions</h1>
          <p className="faint mono" style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
            Last updated: {UPDATED}
          </p>
        </div>
      </section>

      <section className="section" style={{ paddingTop: '2.5rem' }}>
        <div className="shell prose" style={{ maxWidth: '46rem' }}>
          <p>
            These Terms govern use of this website at ennoveda.net, and of VedaJustice, a
            case-management platform operated by Ennoveda Inc. at vedajustice.com. Where a section
            applies to only one of them, it says so.
          </p>

          <h2>Using this website</h2>
          <p>
            The content here is published for general information. It is not a security
            assessment, an audit, or professional advice about your particular environment, and no
            consulting relationship is created by reading it or by sending us a message.
          </p>

          <h2>The domain checker</h2>
          <p>
            The domain checker reads publicly available information — DNS records anyone can query
            and the HTTP headers any visitor receives. It does not scan, probe, or attempt to
            access any system. It is offered as-is, with no warranty that it is complete or
            correct; a passing grade is not a certification and a failing grade is not a
            vulnerability disclosure. Use it on domains you are responsible for.
          </p>

          <h2>SMS / text messaging — VedaJustice</h2>
          <p>
            By opting in to VedaJustice Case Notifications, you agree to receive transactional SMS
            from Ennoveda Inc. (on behalf of the licensing agency) related to your case — court
            dates, deadlines, case-status updates, victim-services reminders, and one-time access
            codes. This is a transactional program; no marketing messages are sent.
          </p>
          <ul>
            <li><strong>Frequency:</strong> Message frequency varies with case activity.</li>
            <li><strong>Cost:</strong> Message and data rates may apply.</li>
            <li>
              <strong>Opt-out:</strong> Reply STOP to cancel at any time. Reply HELP for help, or
              contact <a href={`mailto:${CONTACT.support}`}>{CONTACT.support}</a>.
            </li>
            <li><strong>Carriers:</strong> Carriers are not liable for delayed or undelivered messages.</li>
            <li>
              <strong>Eligibility:</strong> You must be the account holder or authorized user of the
              mobile number provided, and consent is captured at case intake as described in our
              Privacy Policy.
            </li>
          </ul>

          <h2>Acceptable use</h2>
          <p>
            You agree to use our services only for lawful, authorized purposes, and not to misuse,
            disrupt, or attempt to gain unauthorized access to them. If you want to test this site's
            security, read the <a href="/trust">Trust Center</a> first and write to{' '}
            <a href={`mailto:${CONTACT.security}`}>{CONTACT.security}</a> — we would rather hear
            from you than about you.
          </p>

          <h2>Disclaimers and limitation of liability</h2>
          <p>
            Our services are provided “as is” without warranties of any kind to the maximum extent
            permitted by law. Ennoveda is not liable for indirect or consequential damages arising
            from their use.
          </p>

          <h2>Changes</h2>
          <p>
            We may update these Terms from time to time. Continued use after changes constitutes
            acceptance.
          </p>

          <h2>Contact</h2>
          <Address />
          <p><a href={`mailto:${CONTACT.support}`}>{CONTACT.support}</a></p>
        </div>
      </section>
    </>
  );
}
